import { useQueries } from '@tanstack/react-query';
import { memo, useEffect, useMemo, useState } from 'react';

import { useWindowSize } from '@hooks/useWindowSize';
import { getEditorialByIdQuery } from '@queries/editorial';
import VideoPlayer from '@shared/VideoPlayer';

export type PlayerVideoOptions = {
  playerId?: string;
  videoIds?: string[];
};

const App = ({ options }: { options: PlayerVideoOptions }) => {
  const { isMobile, isPortrait } = useWindowSize();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [useSquareVideo, setUseSquareVideo] = useState(isMobile && isPortrait);

  const { videos } = useQueries({
    queries: options.videoIds.map((id) => getEditorialByIdQuery(id)),
    combine: (results) => ({ videos: results.map((result) => result.data).filter(Boolean) }),
  });

  useEffect(() => {
    if (isFullscreen) return;

    setUseSquareVideo(isMobile && isPortrait);
  }, [isFullscreen, isMobile, isPortrait]);

  const squareVideo = useMemo(() => videos?.find((item) => item.width <= item.height), [videos]);
  const wideVideo = useMemo(() => videos?.find((item) => item.width > item.height), [videos]);
  const video = useMemo(() => (useSquareVideo ? squareVideo : wideVideo), [useSquareVideo, squareVideo, wideVideo]);

  useEffect(() => {
    if (!videos.length) return;
    document.querySelector('.js-player-header')?.classList.toggle('player-header--hero', !!video);
    document.querySelector('.js-player-header')?.classList.toggle('player-header--hero-video', !!video);
  }, [video, videos.length]);

  const setJwPlayerRef = (ref: jwplayer.JWPlayer) => {
    if (!ref) return;

    ref.on('fullscreen', (e) => {
      setIsFullscreen(e.fullscreen);
    });
  };

  const aspectRatio = useSquareVideo ? '1:1' : '16:9';

  if (!video) return null;

  return (
    <VideoPlayer
      key={`${video.id}_${aspectRatio}`}
      onJwPlayerInit={setJwPlayerRef}
      isAutoPlay
      isMuted
      repeat
      aspectratio={aspectRatio}
      stretching='fill'
      showTitle={false}
      teaser={video}
      fullscreenOnClick
      unmuteOnFullscreen
    />
  );
};

export default memo(App);
